// @react
import React from 'react'
// @types
import { GetUserQuery, GetUserQueryVariables } from 'lib/graphql/graphqlTypes'
// @graphQL
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import strings from 'lib/constants/strings'
// @components
import Grid from '@bit/atd.web.grid'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module components
import {
  NoAssessment,
  PartialAssessment,
  CompleteAssessment,
  NoStartedPlan,
  NoStartedPath,
  PartialPath,
  CompletePath,
} from './CapabilityCards'
import QueryError from 'modules/common/components/Errors/QueryError'

/**
 * 2.0.0.4 MyATD - Dashboard - Capability Model
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=866%3A3045
 *
 * CapabilityModel	        isSelfAssessmentStarted
 * CapabilityModel	        totalScore
 * CapabilityModel	        isLearningPathSelected
 * CapabilityModel	        learningPath
 * CapabilityModel	        isLearningPlanSelected
 * CapabilityModel      	  totalLearningPlanActivities
 * CapabilityModel	        totalLearningPlanActivitiesCompleted
 * CapabilityModel	        learningPlanComplete
 *
 * CapabilityModel/domain	  progress
 * CapabilityModel/domain	  title
 *
 */

const CapabilityModel = () => {
  /******************************************
   * GQL HOOKS
   ******************************************/

  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, { fetchPolicy: 'cache-first' })

  /******************************************
   * FUNCTIONS
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  let content

  if (!isLoading && data?.user?.capabilityModel) {
    const {
      isSelfAssessmentStarted,
      isSelfAssessmentComplete,
      isLearningPathSelected,
      isLearningPlanCreated,
      isLearningPlanComplete,
      planActivitiesCount,
      planActivitiesCompletedCount,
      learningPathLabel,
      domains,
    } = data.user.capabilityModel

    let leftColumn

    // left column definition

    if (!isSelfAssessmentStarted && !isSelfAssessmentComplete) {
      leftColumn = <NoAssessment />
    } else if (isSelfAssessmentStarted && !isSelfAssessmentComplete) {
      leftColumn =
        !!domains && domains.length ? (
          <PartialAssessment domains={domains} />
        ) : (
          <></>
        )
    } else {
      leftColumn =
        !!domains && domains.length ? (
          <CompleteAssessment domains={domains} />
        ) : (
          <></>
        )
    }

    let rightColumn

    if (!isLearningPathSelected) {
      rightColumn = <NoStartedPath />
    } else if (isLearningPathSelected && !isLearningPlanCreated) {
      rightColumn = <NoStartedPlan pathLabel={learningPathLabel || ''} />
    } else if (
      isLearningPathSelected &&
      isLearningPlanCreated &&
      !isLearningPlanComplete
    ) {
      rightColumn = (
        <PartialPath
          pathLabel={learningPathLabel || ''}
          progress={planActivitiesCompletedCount || 0}
          total={planActivitiesCount || 0}
        />
      )
    } else if (isLearningPathSelected && isLearningPlanComplete) {
      rightColumn = <CompletePath />
    }

    content = (
      <Grid container spacing={2} alignItems="stretch">
        <Grid style={{ display: 'flex' }} item xs={12} md={6}>
          {leftColumn}
        </Grid>
        <Grid style={{ display: 'flex' }} item xs={12} md={6}>
          {rightColumn}
        </Grid>
      </Grid>
    )
  } else {
    content = <SkeletonLoader />
  }

  return (
    <Box
      variant="subModule"
      header={{
        title: strings.DASHBOARD.CAPABILITY.TD_CAPABILITY_MODEL,
      }}
      footer={{
        link: {
          url: 'https://tdcapability.org/#/',
          label: strings.DASHBOARD.CAPABILITY.VIEW_CAPABILITY_MODEL,
        },
      }}
    >
      {content}
    </Box>
  )
}

export default CapabilityModel
